"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import {
  FileText,
  Download,
  User,
  Building2,
  Briefcase,
  Award,
  Crown,
  Heart,
  Target,
  Activity,
  Calendar,
} from "lucide-react"

export interface AveragedRating {
  rateeId: string
  valueAverages: { [key: string]: number }
  leadershipAverages: { [key: string]: number }
  templateAverages: { [key: string]: number }
  overallValueAverage: number
  overallLeadershipAverage: number
  overallTemplateAverage: number
  finalOverallAverage: number
}

const Reports = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [averagedRatings, setAveragedRatings] = useState<AveragedRating[]>([])
  const [description, setDescription] = useState("")
  const [initials, setInitials] = useState("")
  const me = store.auth.meJson

  useEffect(() => {
    const name = me ? me.displayName || " " : " "
    setInitials(
      name
        .split(" ")
        .map((namePart) => namePart[0])
        .join(""),
    )
  }, [me])

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        await api.ratingAssignments.getAll()
        const activeAssignment = store.ratingAssignments.all.find((rating) => rating.asJson.isActive === true)
        const activeDescription = activeAssignment?.asJson.description ?? ""
        setDescription(activeDescription)

        await Promise.all([
          api.valueRating.getAll(me?.uid ?? "", activeDescription),
          api.leadershipRating.getAll(me?.uid ?? "", activeDescription),
          api.templateRating.getAll(),
        ])
      } catch (err) {
        setError("Failed to load data. Please try again.")
        console.error("Error loading data:", err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [api, store, me?.uid])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const inProgressAssignment = store.ratingAssignments.all.find(
          (rate) => rate.asJson.status === "In Progress",
        )?.asJson

        if (inProgressAssignment && users.length && inProgressAssignment.description) {
          const uids = users.map((user) => user.uid)
          await Promise.all([
            api.valueRating.getAllNew(uids, inProgressAssignment.description),
            api.leadershipRating.getAllNew(uids, inProgressAssignment.description),
          ])
        }
        calculateAverages()
      } catch (error) {
        console.error("Error fetching data:", error)
        setError("An error occurred while fetching data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  const calculateAverages = () => {
    const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
    const leadershipRatings = store.leadershipRating.all.map((rating) => rating.asJson)
    const templateRatings = store.templateRating.all.map((rating) => rating.asJson)

    const filteredRatings = store.user.all
      .filter((user) => user.asJson.uid === me?.uid)
      .map((user) => {
        const rateeId = user.asJson.uid
        const userValueRatings = valueRatings.filter((rating) => rating.rateeId === rateeId)
        const userLeadershipRatings = leadershipRatings.filter((rating) => rating.rateeId === rateeId)
        const userTemplateRatings = templateRatings.filter((rating) => rating.rateeId === rateeId)

        const valueAverages: { [key: string]: number } = {}
        const leadershipAverages: { [key: string]: number } = {}
        const templateAverages: { [key: string]: number } = {}

        // Calculate value averages
        userValueRatings.forEach((rating) => {
          Object.entries(rating.values).forEach(([, raterData]) => {
            Object.entries(raterData.ratings).forEach(([valueName, statements]) => {
              if (!valueAverages[valueName]) valueAverages[valueName] = 0
              let total = 0
              let count = 0
              Object.values(statements).forEach((rating: any) => {
                if (Array.isArray(rating)) {
                  total += rating.reduce((sum, val) => sum + val, 0)
                  count += rating.length
                } else {
                  total += rating
                  count++
                }
              })
              valueAverages[valueName] += total / count
            })
          })
        })

        // Calculate leadership averages
        userLeadershipRatings.forEach((rating) => {
          Object.entries(rating.criteria).forEach(([, raterData]) => {
            Object.entries(raterData.ratings).forEach(([criteriaName, rating]) => {
              if (!leadershipAverages[criteriaName]) leadershipAverages[criteriaName] = 0
              if (typeof rating === "number") {
                leadershipAverages[criteriaName] += rating
              } else if (typeof rating === "object") {
                const ratingsArray = Object.values(rating) as number[]
                const avgRating = ratingsArray.reduce((sum, val) => sum + val, 0) / ratingsArray.length
                leadershipAverages[criteriaName] += avgRating
              }
            })
          })
        })

        // Calculate template averages
        userTemplateRatings.forEach((rating) => {
          Object.entries(rating.values).forEach(([, raterData]) => {
            Object.entries(raterData.ratings).forEach(([criteriaName, rating]) => {
              if (!templateAverages[criteriaName]) templateAverages[criteriaName] = 0
              if (typeof rating === "number") {
                templateAverages[criteriaName] += rating
              } else if (typeof rating === "object") {
                const ratingsArray = Object.values(rating) as number[]
                const avgRating = ratingsArray.reduce((sum, val) => sum + val, 0) / ratingsArray.length
                templateAverages[criteriaName] += avgRating
              }
            })
          })
        })

        // Finalize averages
        Object.keys(valueAverages).forEach((key) => {
          valueAverages[key] /= userValueRatings.length || 1
        })
        Object.keys(leadershipAverages).forEach((key) => {
          leadershipAverages[key] /= userLeadershipRatings.length || 1
        })
        Object.keys(templateAverages).forEach((key) => {
          templateAverages[key] /= userTemplateRatings.length || 1
        })

        const overallValueAverage =
          Object.values(valueAverages).reduce((sum, val) => sum + val, 0) / (Object.keys(valueAverages).length || 1)
        const overallLeadershipAverage =
          Object.values(leadershipAverages).reduce((sum, val) => sum + val, 0) /
          (Object.keys(leadershipAverages).length || 1)
        const overallTemplateAverage =
          Object.values(templateAverages).reduce((sum, val) => sum + val, 0) /
          (Object.keys(templateAverages).length || 1)

        const finalOverallAverage = (overallValueAverage + overallLeadershipAverage + overallTemplateAverage) / 3

        return {
          rateeId,
          valueAverages,
          leadershipAverages,
          templateAverages,
          overallValueAverage,
          overallLeadershipAverage,
          overallTemplateAverage,
          finalOverallAverage,
        }
      })

    setAveragedRatings(filteredRatings.sort((a, b) => b.finalOverallAverage - a.finalOverallAverage))
  }

  const getDisplayNameById = (userId: string) => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user ? user.displayName : "Unknown User"
  }

  const getDepartmentName = (departmentId: string): string => {
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : ""
  }

  const handleExportPDF = () => {
    console.log("Export PDF functionality")
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <FileText className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Reports</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <FileText className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Performance Reports</h2>
            <p className="text-slate-600">Comprehensive performance analysis and insights</p>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid gap-8">
        {averagedRatings.map((rating) => {
          const {
            rateeId,
            valueAverages,
            leadershipAverages,
            templateAverages,
            overallValueAverage,
            overallLeadershipAverage,
            overallTemplateAverage,
            finalOverallAverage,
          } = rating

          const rateeName = getDisplayNameById(rateeId)
          const departmentName = getDepartmentName(
            store.user.all.find((user) => user.asJson.uid === rateeId)?.asJson.department || "",
          )
          const jobTitle =
            store.user.all.find((user) => user.asJson.uid === rateeId)?.asJson.jobTitle || "Not specified"

          return (
            <div key={rateeId} className="bg-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
              {/* Header Section */}
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 border-b border-slate-200/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    {/* User Avatar */}
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-bold text-white">{initials}</span>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-slate-800">{rateeName}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>Assessment: {description}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{departmentName}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Briefcase className="h-4 w-4 text-purple-600" />
                          <span className="font-medium">{jobTitle}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Export Button */}
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <Download className="h-4 w-4" />
                    <span className="font-semibold">Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Overall Score */}
                  <div className="lg:col-span-1">
                    <div className="text-center space-y-4">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Award className="h-5 w-5 text-amber-600" />
                        <h4 className="text-lg font-semibold text-slate-800">Overall Rating</h4>
                      </div>

                      {/* Circular Progress */}
                      <div className="relative w-32 h-32 mx-auto">
                        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                          <circle
                            cx="60"
                            cy="60"
                            r="50"
                            stroke="#e2e8f0"
                            strokeWidth="8"
                            fill="transparent"
                            className="opacity-20"
                          />
                          <circle
                            cx="60"
                            cy="60"
                            r="50"
                            stroke="url(#gradient)"
                            strokeWidth="8"
                            fill="transparent"
                            strokeDasharray={`${(finalOverallAverage / 5) * 314} 314`}
                            strokeLinecap="round"
                            className="transition-all duration-1000 ease-out"
                          />
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#3B82F6" />
                              <stop offset="100%" stopColor="#8B5CF6" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-slate-800">{finalOverallAverage.toFixed(1)}</div>
                            <div className="text-xs text-slate-500">out of 5.0</div>
                          </div>
                        </div>
                      </div>

                      <div className="text-sm text-slate-600">
                        {finalOverallAverage >= 4.5
                          ? "Exceptional"
                          : finalOverallAverage >= 4
                            ? "Excellent"
                            : finalOverallAverage >= 3.5
                              ? "Good"
                              : finalOverallAverage >= 3
                                ? "Satisfactory"
                                : "Needs Improvement"}
                      </div>
                    </div>
                  </div>

                  {/* Rating Categories */}
                  <div className="lg:col-span-3 space-y-6">
                    {/* Values Section */}
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Heart className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800">Values Ratings</h4>
                          <p className="text-sm text-slate-600">
                            Average: <span className="font-semibold">{overallValueAverage.toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(valueAverages).map(([valueName, average]) => (
                          <div key={valueName} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <span className="text-sm font-medium text-slate-700">{valueName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800">{average.toFixed(2)}</span>
                              <div className="w-12 bg-slate-200 rounded-full h-2">
                                <div
                                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                  style={{ width: `${(average / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Leadership Section */}
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Crown className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800">Leadership Ratings</h4>
                          <p className="text-sm text-slate-600">
                            Average: <span className="font-semibold">{overallLeadershipAverage.toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(leadershipAverages).map(([criteriaName, average]) => (
                          <div key={criteriaName} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <span className="text-sm font-medium text-slate-700">{criteriaName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800">{average.toFixed(2)}</span>
                              <div className="w-12 bg-slate-200 rounded-full h-2">
                                <div
                                  className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                  style={{ width: `${(average / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Template Section */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Target className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800">Professional Competency</h4>
                          <p className="text-sm text-slate-600">
                            Average: <span className="font-semibold">{overallTemplateAverage.toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(templateAverages).map(([criteriaName, average]) => (
                          <div key={criteriaName} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <span className="text-sm font-medium text-slate-700">{criteriaName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800">{average.toFixed(2)}</span>
                              <div className="w-12 bg-slate-200 rounded-full h-2">
                                <div
                                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                                  style={{ width: `${(average / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty State */}
      {averagedRatings.length === 0 && (
        <div className="text-center py-12">
          <div className="p-4 bg-slate-100 rounded-full w-fit mx-auto mb-4">
            <FileText className="h-12 w-12 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Reports Available</h3>
          <p className="text-slate-600">Complete some ratings to generate your performance report.</p>
        </div>
      )}
    </div>
  )
})

export default Reports
